import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { ApiUrls } from '../api-url';

@Injectable({
  providedIn: 'root'
})
export class AppConfigService {

  private config: any;
  private companyConfiguration: any;

  constructor(private handler: HttpBackend) {
    this.http = new HttpClient(handler);
  }

  private http: HttpClient;

  load(): Promise<void> {
    return firstValueFrom(
      this.http.get('assets/config.json')
    ).then((data: any) => {
      this.config = data;
    }).catch(err => {
      console.error('❌ Failed to load config:', err);
      throw err;
    });
  }

  async loadCompanyConfiguration(): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this.http.get(this.masterBaseUrl + ApiUrls.CompanyConfiguration.GetCompanyConfig, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        })
      );

      if (response?.data) {
        this.companyConfiguration = response.data;
      }
    } catch (err) {
      console.error('Failed to load company configuration', err);
      this.companyConfiguration = {};
    }
  }

  get companyLogo() {
    return this.companyConfiguration?.logoURL || this.config?.logoURL;
  }

  // currency
  get companyCurrency() {
    return this.companyConfiguration?.currency || this.config?.currency;
  }

  private ensure() {
    if (!this.config) {
      throw new Error('Config not loaded!');
    }
  }

  get production(): boolean {
    this.ensure();
    return this.config.production;
  }

  get bypassAuth(): boolean {
    this.ensure();
    return this.config.bypassAuth;
  }

  get apiUrl(): string {
    this.ensure();
    return this.config.apiUrl;
  }

  get apiUrl2(): string {
    this.ensure();
    return this.config.apiUrl2;
  }

  get allowedDomains(): string[] {
    this.ensure();
    return this.config.allowedDomains || [];
  }

  get masterBaseUrl(): string {
    this.ensure();
    return this.config.masterBaseUrl;
  }

  get hubUrl(): string {
    this.ensure();
    return this.config.hubUrl;
  }

  get portalUrl(): string {
    this.ensure();
    return this.config.portalUrl;
  }

  get googleMapKey(): string {
    this.ensure();
    return this.config.googleMapKey;
  }

  // No ensure() here: SSO cookies may be read before config.json finishes loading
  get env(): string {
    return this.config?.env || '';
  }

  // Domain of the SSO cookies written by the Hub, prefixed with env (e.g. .dev.markaziahub.com)
  get ssoCookieDomain(): string {
    const env = this.env;
    return env ? `.${env}.markaziahub.com` : '.markaziahub.com';
  }

  get version(): string {
    this.ensure();
    return this.config.version;
  }

  get currentSystem(): string {
    this.ensure();
    return this.config.currentSystem;
  }

  get isTareeqAlmajalSystem(): boolean {
    this.ensure();
    return this.config.currentSystem === 'TareeqAlmajal';
  }

  get token(): string | null {
    this.ensure();

    if (this.config.bypassAuth) {
      return this.config?.staticAuth?.token || null;
    }

    return localStorage.getItem('token');
  }

  get staticAuth(): any {
    // for local development bypass, not for production use
    this.ensure();
    return this.config.staticAuth || null;
  }

  get userID(): number | null {
    this.ensure();

    if (this.config.bypassAuth) {
      try {
        const payload = JSON.parse(atob(this.config.staticAuth.token.split('.')[1]));
        return payload?.nameid || null;
      } catch {
        return null;
      }
    }

    const user = localStorage.getItem('currentUser');
    return user ? JSON.parse(user)?.nameid : null;
  }
}